import React, { useState, useEffect } from 'react';
import { portfolioData } from '../data/mock';
import { Trophy, ExternalLink, Github, Zap, Clock, ShieldCheck, Flame, Cpu, ChevronLeft, ChevronRight } from 'lucide-react';
import useAnalytics from '../hooks/useAnalytics';
import DecryptedText from './reactbits/DecryptedText';
import SpotlightCard from './reactbits/SpotlightCard';
import BlurText from './reactbits/BlurText';
import './Hackathons.css';

const Hackathons = () => {
  const { trackEvent } = useAnalytics();
  const { hackathons } = portfolioData;
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoplay, setIsAutoplay] = useState(true);

  useEffect(() => {
    if (!isAutoplay) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev === hackathons.length - 1 ? 0 : prev + 1));
    }, 2000);

    return () => clearInterval(timer);
  }, [isAutoplay, hackathons.length]);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev === 0 ? hackathons.length - 1 : prev - 1));
    trackEvent('hackathon_carousel', { action: 'prev' });
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev === hackathons.length - 1 ? 0 : prev + 1));
    trackEvent('hackathon_carousel', { action: 'next' });
  };

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  const minSwipeDistance = 40;

  const onTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
    setIsAutoplay(false);
  };

  const onTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    if (distance > minSwipeDistance) {
      handleNext();
    } else if (distance < -minSwipeDistance) {
      handlePrev();
    }
  };

  return (
    <section className="hackathons-section" id="hackathons">
      <div className="hackathons-bg-pattern"></div>

      <div className="hackathons-container">
        {/* Header */}
        <div className="section-header">
          <div className="header-line"></div>
          <h2 className="section-title">
            <span className="title-bracket">{'<'}</span>
            <DecryptedText text="Hackathon Battle Arena" speed={40} maxIterations={12} animateOn="view" />
            <span className="title-bracket">{'/>'}</span>
          </h2>
          <div className="section-subtitle">
            <BlurText text="Competitive sprint builds, rapid prototypes & hackathon battle nodes" delay={60} animateBy="words" />
          </div>
        </div>

        {/* Carousel Wrapper with Controls & Mobile Touch Gestures */}
        <div
          className="hackathon-carousel-wrapper"
          onMouseEnter={() => setIsAutoplay(false)}
          onMouseLeave={() => setIsAutoplay(true)}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
        >

          {/* Left Arrow Button */}
          <button
            className="carousel-arrow-btn left-arrow"
            onClick={handlePrev}
            aria-label="Previous Hackathon"
          >
            <ChevronLeft className="arrow-icon" />
          </button>

          {/* Carousel Window */}
          <div className="hackathon-carousel-viewport">
            <div
              className="hackathon-carousel-track"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {hackathons.map((item, index) => (
                <div key={item.id} className="hackathon-slide">
                  <SpotlightCard
                    className="hackathon-battle-card"
                    spotlightColor="rgba(0, 255, 209, 0.14)"
                  >
                    <div className="battle-card-inner">
                      {/* Event Top Badge Ribbon */}
                      <div className="battle-top-bar">
                        <div className="event-name-tag">
                          <Flame className="arena-fire-icon" />
                          <span>{item.status?.toUpperCase() || 'HACKATHON BUILD'}</span>
                        </div>

                        <div className="sprint-time-tag">
                          <Clock className="clock-icon" />
                          <span>24-48 HOUR SPRINT</span>
                        </div>
                      </div>

                      <div className="battle-card-content">
                        {/* Left Holographic Media Container */}
                        <div className="battle-media-box">
                          <img
                            src={item.image}
                            alt={item.title}
                            className="battle-img"
                            loading="lazy"
                          />
                          <div className="media-scanlines"></div>
                          <div className="media-hud-overlay">
                            <div className="hud-badge">
                              <Trophy className="hud-trophy" />
                              <span>SPRINT PROTOTYPE</span>
                            </div>
                          </div>
                        </div>

                        {/* Right Battle Specs Container */}
                        <div className="battle-specs-box">
                          <div className="battle-title-row">
                            <h3 className="battle-item-title">{item.title}</h3>
                            <span className="node-id-tag">NODE_0{index + 1}</span>
                          </div>

                          <p className="battle-description">{item.description}</p>

                          {/* Rapid Tech Stack Badges */}
                          <div className="battle-tech-stack">
                            <div className="tech-stack-label">
                              <Cpu className="cpu-icon" />
                              <span>RAPID_BUILD_STACK:</span>
                            </div>
                            <div className="tech-pills-row">
                              {item.technologies.map((tech, techIdx) => (
                                <span key={techIdx} className="arena-tech-pill">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Hackathon Battle Metrics Row */}
                          <div className="battle-metrics-bar">
                            <div className="metric-cell">
                              <span className="metric-lbl">VERIFICATION</span>
                              <span className="metric-val green">
                                <ShieldCheck className="mini-icon" /> AUTHENTICATED
                              </span>
                            </div>
                            <div className="metric-cell">
                              <span className="metric-lbl">SPRINT TYPE</span>
                              <span className="metric-val cyan">
                                <Zap className="mini-icon" /> RAPID PROTOTYPE
                              </span>
                            </div>
                          </div>

                          {/* Arena Action Buttons */}
                          <div className="battle-actions">
                            <a
                              href={item.demoUrl || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`arena-btn primary ${!item.demoUrl ? 'disabled-btn' : ''}`}
                              onClick={(e) => {
                                if (!item.demoUrl) {
                                  e.preventDefault();
                                  alert(`Live node preview for "${item.title}" is archived in hackathon records.`);
                                } else {
                                  trackEvent('hackathon_click', { project: item.title, action: 'demo' });
                                }
                              }}
                            >
                              <ExternalLink className="btn-icon" />
                              <span>DEMO NODE</span>
                            </a>

                            <a
                              href={item.githubUrl || '#'}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`arena-btn secondary ${!item.githubUrl ? 'disabled-btn' : ''}`}
                              onClick={(e) => {
                                if (!item.githubUrl) {
                                  e.preventDefault();
                                  alert(`Source repository for "${item.title}" is logged in official records.`);
                                } else {
                                  trackEvent('hackathon_click', { project: item.title, action: 'source' });
                                }
                              }}
                            >
                              <Github className="btn-icon" />
                              <span>SOURCE CODE</span>
                            </a>
                          </div>
                        </div>
                      </div>

                      {/* Cyber Corner Accents */}
                      <div className="corner-accent top-l"></div>
                      <div className="corner-accent top-r"></div>
                      <div className="corner-accent bot-l"></div>
                      <div className="corner-accent bot-r"></div>
                    </div>
                  </SpotlightCard>
                </div>
              ))}
            </div>
          </div>

          {/* Right Arrow Button */}
          <button
            className="carousel-arrow-btn right-arrow"
            onClick={handleNext}
            aria-label="Next Hackathon"
          >
            <ChevronRight className="arrow-icon" />
          </button>
        </div>

        {/* Carousel Pagination & Node Indicators */}
        <div className="carousel-nav-footer">
          <div className="carousel-dots-row">
            {hackathons.map((_, idx) => (
              <button
                key={idx}
                className={`carousel-node-dot ${currentIndex === idx ? 'active' : ''}`}
                onClick={() => setCurrentIndex(idx)}
                aria-label={`Go to slide ${idx + 1}`}
              >
                <span>NODE 0{idx + 1}</span>
              </button>
            ))}
          </div>

          <div className="carousel-counter-badge">
            <span>NODE {String(currentIndex + 1).padStart(2, '0')} / {String(hackathons.length).padStart(2, '0')}</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hackathons;
